import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Empty, Label } from '../ui';
import { dayLabel, elapsed, periodState, rangeLabel, toDay } from './dates';
import { sprintShort } from './colors';

// Roadmap sur téléphone : les itérations en cours, puis à venir (les terminées repliées), chacune avec ses sprints.

const IterationCard = ({ iteration, color, today, onOpen }) => {
  const start = toDay(iteration.start_date);
  const end = toDay(iteration.end_date);
  const state = periodState(start, end, today);
  const total = Number(iteration.total_tokens) || 0;
  return (
    <button type="button" onClick={() => onOpen(iteration.id)}
      className="block w-full rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm active:bg-slate-50">
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">{iteration.name}</span>
        <span className="shrink-0 text-xs text-slate-400">{start != null && end != null ? rangeLabel(start, end) : ''}</span>
      </div>
      {iteration.description && <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-slate-600">{iteration.description}</p>}
      {state === 'current' && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${color.strong}`} style={{ width: `${Math.round(elapsed(start, end, today) * 100)}%` }} />
          </div>
          encore {end - today} j
        </div>
      )}
      {iteration.sprints.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {iteration.sprints.map((sprint, index) => {
            const current = periodState(toDay(sprint.start_date), toDay(sprint.end_date), today) === 'current';
            return (
              <span key={sprint.id} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${current ? `${color.soft} ${color.text}` : 'bg-slate-100 text-slate-500'}`}>
                {sprintShort(sprint, index)}
              </span>
            );
          })}
          {total > 0 && <span className="ml-auto text-[11px] tabular-nums text-slate-400">{iteration.completed_tokens}/{total} pts</span>}
        </div>
      )}
    </button>
  );
};

const RoadmapList = ({ iterations, milestones, colors, today, onOpen, onOpenMilestone }) => {
  const [showDone, setShowDone] = useState(false);
  const groups = { current: [], upcoming: [], done: [], undated: [] };
  iterations.forEach((it) => groups[periodState(toDay(it.start_date), toDay(it.end_date), today)].push(it));
  const upcomingMilestones = milestones.filter((m) => m.status !== 'done').slice(0, 4);
  const card = (it) => <IterationCard key={it.id} iteration={it} color={colors.get(it.id)} today={today} onOpen={onOpen} />;
  if (!iterations.length && !milestones.length) return <Empty>Pas encore d'itération.</Empty>;
  return (
    <div className="space-y-4">
      {upcomingMilestones.length > 0 && (
        <div>
          <Label className="mb-1.5">Prochains jalons</Label>
          <ul className="space-y-1">
            {upcomingMilestones.map((m) => {
              const day = toDay(m.milestone_date);
              return (
                <li key={m.id}>
                  <button type="button" onClick={() => onOpenMilestone(m)} className="flex w-full items-center gap-2 text-left text-[13px]">
                    <span className={`h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] ${day < today ? 'bg-rose-500' : 'bg-rose-400'}`} />
                    <span className={`w-14 shrink-0 text-[11px] tabular-nums ${day < today ? 'font-medium text-rose-600' : 'text-slate-400'}`}>{dayLabel(day)}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-700">{m.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {[...groups.current, ...groups.undated].length > 0 && (
        <div className="space-y-2"><Label>En cours</Label>{[...groups.current, ...groups.undated].map(card)}</div>
      )}
      {groups.upcoming.length > 0 && <div className="space-y-2"><Label>À venir</Label>{groups.upcoming.map(card)}</div>}
      {groups.done.length > 0 && (
        <div className="space-y-2">
          <button type="button" onClick={() => setShowDone((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-slate-500">
            <ChevronDown size={13} className={showDone ? 'rotate-180' : ''} /> {groups.done.length} terminée{groups.done.length > 1 ? 's' : ''}
          </button>
          {showDone && groups.done.slice().reverse().map(card)}
        </div>
      )}
    </div>
  );
};

export default RoadmapList;
